<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="'Báo cáo thưởng AdHoc'">
          <template v-slot:preview>
            <div class="report-filter mb-5">
              <b-row class="align-items-end">
                <b-col md="2">
                  <label class="report-filter__label">Từ ngày</label>
                  <date-picker
                    placeholder="Từ ngày"
                    class="form-control form-control-sm"
                    :config="dpConfigs.date"
                    v-model="filter.fromDate"
                  ></date-picker>
                </b-col>
                <b-col md="2">
                  <label class="report-filter__label">Đến ngày</label>
                  <date-picker
                    placeholder="Đến ngày"
                    class="form-control form-control-sm"
                    :config="dpConfigs.date"
                    v-model="filter.toDate"
                  ></date-picker>
                </b-col>
                <b-col md="4">
                  <label class="report-filter__label">Cửa hàng</label>
                  <Autosuggest
                    :model.sync="filter.storeText"
                    :suggestions="filteredStores"
                    placeholder="cửa hàng"
                    :limit="10"
                    @select="onSelectedStore"
                    @change="onInputStore"
                    suggestionName="suggestionName"
                  />
                </b-col>
                <b-col md="4">
                  <b-button
                    variant="primary"
                    size="sm"
                    class="px-6"
                    :disabled="onLoading"
                    @click="onFilter"
                    >Lọc</b-button
                  >
                </b-col>
              </b-row>
            </div>

            <vcl-table v-if="onLoading" :speed="5" :animate="true" :columns="6"></vcl-table>

            <div v-else-if="!hasSearched" class="report-placeholder">
              Chọn kỳ và cửa hàng rồi bấm Lọc để xem báo cáo
            </div>

            <div v-else>
              <!-- Tổng đã chi + các số đếm -->
              <div class="report-summary mb-5">
                <div class="report-summary__total">
                  <div class="report-summary__label">Tổng thưởng AdHoc</div>
                  <div class="report-summary__total-value">
                    {{ displayAmount(totals.amount) }}
                  </div>
                  <div class="report-summary__meta">
                    {{ storeName }} · {{ displayPeriod(period) }}
                  </div>
                </div>
                <div class="report-summary__parts">
                  <div
                    v-for="part in summaryParts"
                    :key="part.key"
                    class="report-summary__part"
                  >
                    <div class="report-summary__label">{{ part.label }}</div>
                    <div class="report-summary__part-value">{{ part.value }}</div>
                  </div>
                </div>
              </div>

              <div v-if="!programs.length" class="report-placeholder">
                Không có thưởng AdHoc nào phát sinh trong kỳ
              </div>

              <template v-else>
                <!-- Theo chương trình: mỗi chương trình một thẻ, bên trong là sản phẩm -->
                <div class="report-section">
                  Theo chương trình
                  <span class="report-section__count">{{ programs.length }}</span>
                </div>
                <div
                  v-for="program in programs"
                  :key="program.id"
                  class="report-card mb-4"
                >
                  <div class="report-card__head">
                    <div>
                      <div class="report-card__name">{{ program.name }}</div>
                      <div class="report-card__meta">
                        {{ displayDate(program.startDate) }} —
                        {{ displayDate(program.endDate) || 'không giới hạn' }} ·
                        {{ displayQuantity(program.billCount) }} hoá đơn ·
                        {{ displayQuantity(program.employeeCount) }} nhân sự
                      </div>
                    </div>
                    <div class="report-card__total">
                      {{ displayAmount(program.amount) }}
                    </div>
                  </div>
                  <b-table
                    class="mb-0"
                    :items="program.items"
                    :fields="itemFields"
                    small
                    borderless
                  >
                    <template v-slot:cell(productName)="row">
                      {{ row.item.productName || '--' }}
                    </template>
                    <template v-slot:cell(adhocRewardModeName)="row">
                      <span
                        class="label label-inline font-weight-bold"
                        :class="adhocModeVariant(row.item.adhocRewardMode)"
                        >{{ row.item.adhocRewardModeName }}</span
                      >
                      <div
                        v-if="Number(row.item.replacedAmount) > 0"
                        class="text-muted small mt-1"
                      >
                        thay hoa hồng thường
                        {{ displayAmount(row.item.replacedAmount) }}
                      </div>
                    </template>
                    <template v-slot:cell(quantity)="row">
                      {{ displayQuantity(row.item.quantity) }}
                    </template>
                    <template v-slot:cell(amount)="row">
                      <span class="font-weight-bold" :class="amountClass(row.item.amount)">
                        {{ displayAmount(row.item.amount) }}
                      </span>
                    </template>
                  </b-table>
                </div>

                <!-- Theo nhân viên: ai nhận bao nhiêu -->
                <div class="report-section mt-6">
                  Theo nhân viên
                  <span class="report-section__count">{{ employees.length }}</span>
                </div>
                <b-table
                  :items="employees"
                  :fields="employeeFields"
                  bordered
                  hover
                >
                  <template v-slot:cell(employeeName)="row">
                    <div class="font-weight-bold text-dark">
                      {{ row.item.employeeName }}
                    </div>
                    <div class="text-muted small">
                      {{ row.item.employeeCode }} · {{ row.item.jobTitleName }}
                    </div>
                  </template>
                  <template v-slot:cell(lineCount)="row">
                    {{ displayQuantity(row.item.lineCount) }}
                  </template>
                  <template v-slot:cell(amount)="row">
                    <span class="font-weight-bold" :class="amountClass(row.item.amount)">
                      {{ displayAmount(row.item.amount) }}
                    </span>
                  </template>
                  <template v-slot:cell(actions)="row">
                    <b-button
                      size="sm"
                      variant="link"
                      class="p-0"
                      title="Xem chi tiết hoa hồng của nhân viên"
                      @click="goToDetail(row.item)"
                    >
                      <i class="fas fa-eye text-primary"></i>
                    </b-button>
                  </template>
                </b-table>
              </template>
            </div>
          </template>
        </KTCodePreview>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Báo cáo thưởng AdHoc — tách riêng khỏi báo cáo hoa hồng để thấy AdHoc đã chi bao nhiêu:
 * tổng, theo chương trình (bung xuống sản phẩm + ghi đè / cộng thêm) và theo nhân viên.
 * Số liệu lấy từ cùng engine nên tổng ở đây bằng cột AdHoc của báo cáo hoa hồng cửa hàng.
 */
import KTCodePreview from '@/view/content/CodePreview.vue';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import moment from 'moment';
import datePicker from 'vue-bootstrap-datetimepicker';
import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css';
import { VclTable } from 'vue-content-loading';
import { makeToastFaile, removeAccents } from '@/utils/common';
import {
  COMMISSION_REPORT_TYPE,
  COMMISSION_ADHOC_REWARD_MODE,
} from './commission.constants';
import commissionReportMixin, {
  DATE_INPUT_FORMAT,
  thStyle,
} from './commissionReport.mixin';

const EMPTY_TOTALS = Object.freeze({
  amount: 0,
  quantity: 0,
  replacedAmount: 0,
  programCount: 0,
  billCount: 0,
  employeeCount: 0,
});

export default {
  components: { KTCodePreview, Autosuggest, VclTable, datePicker },
  mixins: [commissionReportMixin],
  data() {
    return {
      onLoading: false,
      hasSearched: false,
      period: null,
      store: null,
      totals: { ...EMPTY_TOTALS },
      programs: [],
      employees: [],
      storeOptions: [],
      filteredStores: [],
      filter: {
        fromDate: moment().startOf('month').format(DATE_INPUT_FORMAT),
        toDate: moment().format(DATE_INPUT_FORMAT),
        storeId: null,
        storeText: '',
      },
      itemFields: [
        { key: 'productName', label: 'Sản phẩm', thStyle: thStyle('50%') },
        { key: 'adhocRewardModeName', label: 'Cách tính', thStyle: thStyle('22%') },
        {
          key: 'quantity',
          label: 'SL bán',
          thStyle: thStyle('10%'),
          tdClass: 'text-center',
        },
        {
          key: 'amount',
          label: 'Thưởng AdHoc',
          thStyle: thStyle('18%'),
          tdClass: 'text-right',
        },
      ],
      employeeFields: [
        { key: 'employeeName', label: 'Nhân viên', thStyle: thStyle('54%') },
        {
          key: 'lineCount',
          label: 'Số dòng thưởng',
          thStyle: thStyle('18%'),
          tdClass: 'text-center',
        },
        {
          key: 'amount',
          label: 'Thưởng AdHoc',
          thStyle: thStyle('22%'),
          tdClass: 'text-right',
        },
        { key: 'actions', label: '', thStyle: thStyle('6%'), tdClass: 'text-center' },
      ],
    };
  },
  computed: {
    storeName() {
      return this.store ? this.store.name : '';
    },
    summaryParts() {
      return [
        { key: 'programCount', label: 'Chương trình', value: this.totals.programCount },
        { key: 'billCount', label: 'Hoá đơn', value: this.totals.billCount },
        { key: 'quantity', label: 'Sản phẩm bán', value: this.totals.quantity },
        { key: 'employeeCount', label: 'Nhân sự nhận', value: this.totals.employeeCount },
      ].map((part) => ({ ...part, value: this.displayQuantity(part.value) }));
    },
  },
  created() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Hoa hồng V2' },
      { title: 'Báo cáo thưởng AdHoc' },
    ]);
    this.restoreFilterFromQuery();
    this.fetchStores();
  },
  methods: {
    adhocModeVariant(mode) {
      return Number(mode) === COMMISSION_ADHOC_REWARD_MODE.PLUS
        ? 'label-light-info'
        : 'label-light-warning';
    },

    /* ------------------------------ Bộ lọc -------------------------------- */
    restoreFilterFromQuery() {
      const query = this.$route.query || {};
      if (query.fromDate) {
        this.filter.fromDate = query.fromDate;
      }
      if (query.toDate) {
        this.filter.toDate = query.toDate;
      }
      if (query.storeId) {
        this.filter.storeId = Number(query.storeId);
      }
    },
    /** Ghi filter lên URL để F5 / quay lại không mất */
    syncFilterToQuery() {
      const query = {
        fromDate: this.filter.fromDate || undefined,
        toDate: this.filter.toDate || undefined,
        storeId: this.filter.storeId || undefined,
      };
      this.$router.replace({ query }).catch(() => {});
    },
    fetchStores() {
      ApiService.setHeader();
      ApiService.get('/stores/getStores')
        .then(({ data }) => {
          this.storeOptions = (data.data || []).map((store) => ({
            id: store.id,
            name: store.name,
            shortName: store.shortName || '',
            suggestionName: store.shortName
              ? `${store.shortName} (${store.name})`
              : store.name,
          }));
          this.filteredStores = this.filterStoreOptions('');
          // Cửa hàng từ URL: có danh sách rồi mới gán được tên và tải báo cáo
          const selected = this.storeOptions.find(
            (store) => store.id === this.filter.storeId,
          );
          if (selected) {
            this.filter.storeText = selected.suggestionName;
            this.fetchReport();
          }
        })
        .catch(() => makeToastFaile('Không tải được danh sách cửa hàng!'));
    },
    filterStoreOptions(text) {
      const keyword = removeAccents(text || '').toLowerCase();
      return this.storeOptions
        .filter(
          (store) =>
            removeAccents(store.suggestionName).toLowerCase().indexOf(keyword) > -1,
        )
        .slice(0, 10);
    },
    onInputStore(text) {
      // Gõ khác tên cửa hàng đang chọn là bỏ lựa chọn cũ
      const selected = this.storeOptions.find(
        (store) => store.id === this.filter.storeId,
      );
      if (!selected || selected.suggestionName !== text) {
        this.filter.storeId = null;
      }
      this.filteredStores = this.filterStoreOptions(text);
    },
    onSelectedStore(option) {
      this.filter.storeId = option.item.id;
      this.filter.storeText = option.item.suggestionName;
    },

    /* ----------------------------- Tải dữ liệu ----------------------------- */
    onFilter() {
      const fromDate = this.toApiDate(this.filter.fromDate);
      const toDate = this.toApiDate(this.filter.toDate);
      if (!fromDate || !toDate) {
        return makeToastFaile('Vui lòng chọn kỳ báo cáo!');
      }
      if (!this.filter.storeId) {
        return makeToastFaile('Vui lòng chọn cửa hàng!');
      }
      this.syncFilterToQuery();
      this.fetchReport();
    },
    fetchReport() {
      this.onLoading = true;
      ApiService.setHeader();
      ApiService.query('commission-reports/adhoc', {
        params: {
          storeId: this.filter.storeId,
          fromDate: this.toApiDate(this.filter.fromDate),
          toDate: this.toApiDate(this.filter.toDate),
        },
      })
        .then(({ data }) => {
          const result = data.data || {};
          this.period = result.period || null;
          this.store = result.store || null;
          this.totals = { ...EMPTY_TOTALS, ...(result.totals || {}) };
          this.programs = result.programs || [];
          this.employees = result.employees || [];
          this.hasSearched = true;
        })
        .catch((error) => {
          this.programs = [];
          this.employees = [];
          this.totals = { ...EMPTY_TOTALS };
          makeToastFaile(
            this.getErrorMessage(error, 'Không tải được báo cáo thưởng AdHoc!'),
          );
        })
        .finally(() => {
          this.onLoading = false;
        });
    },
    goToDetail(item) {
      this.$router.push({
        name: 'commission-report-detail',
        query: {
          type: COMMISSION_REPORT_TYPE.STORE,
          employeeId: item.employeeId,
          fromDate: this.toApiDate(this.filter.fromDate),
          toDate: this.toApiDate(this.filter.toDate),
          storeId: this.filter.storeId,
        },
      });
    },
  },
};
</script>

<style scoped>
.report-filter {
  background-color: #f8f9fb;
  border: 1px solid #ecf0f3;
  border-radius: 0.42rem;
  padding: 1rem 1.25rem;
}
.report-filter__label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #7e8299;
  margin-bottom: 0.3rem;
}
.report-placeholder {
  text-align: center;
  color: #7e8299;
  padding: 3rem 0;
}

.report-summary {
  display: flex;
  flex-wrap: wrap;
  border: 1px solid #ecf0f3;
  border-radius: 0.42rem;
  overflow: hidden;
}
.report-summary__total {
  flex: 0 0 320px;
  padding: 1.25rem 1.5rem;
  background-color: #f1faff;
  border-right: 1px solid #ecf0f3;
}
.report-summary__total-value {
  font-size: 1.9rem;
  font-weight: 700;
  line-height: 1.2;
  color: #187de4;
}
.report-summary__meta {
  margin-top: 0.4rem;
  font-size: 0.85rem;
  color: #7e8299;
}
.report-summary__parts {
  flex: 1 1 420px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
}
.report-summary__part {
  padding: 1.25rem;
  border-right: 1px solid #ecf0f3;
}
.report-summary__part:last-child {
  border-right: 0;
}
.report-summary__label {
  font-size: 0.85rem;
  color: #7e8299;
  margin-bottom: 0.3rem;
}
.report-summary__part-value {
  font-size: 1.15rem;
  font-weight: 600;
  color: #181c32;
}

.report-section {
  font-size: 13px;
  font-weight: 600;
  color: #181c32;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 12px;
}
.report-section__count {
  display: inline-block;
  min-width: 20px;
  padding: 0 6px;
  margin-left: 4px;
  border-radius: 10px;
  background: #ecf0f3;
  color: #7e8299;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
}

.report-card {
  border: 1px solid #ecf0f3;
  border-radius: 0.42rem;
  overflow: hidden;
}
.report-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #f8f9fb;
  padding: 12px 16px;
  border-bottom: 1px solid #ecf0f3;
}
.report-card__name {
  font-weight: 600;
  color: #181c32;
}
.report-card__meta {
  font-size: 12px;
  color: #7e8299;
  margin-top: 2px;
}
.report-card__total {
  font-size: 1.15rem;
  font-weight: 700;
  color: #181c32;
  white-space: nowrap;
}
</style>
